/**
 * RTK Query base API — B4 full implementation.
 *
 * Replaces the B3 placeholder with:
 *   - Custom `baseQueryWithReauth` (cookie auth, CSRF, client headers,
 *     single-flight 401 → refresh → retry, 410 cache invalidation)
 *   - Full `tagTypes` covering all entity types used across injected endpoints
 *   - `refetchOnFocus` + `refetchOnReconnect` enabled for freshness
 *
 * Endpoints are injected by feature slices (e.g. postsApi.ts, usersApi.ts).
 * Declaring `tagTypes` here is required so tag-based cache invalidation works
 * across separately injected endpoint files.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  /**
   * All entity types used across injected endpoint slices.
   * - Post / PublicPost — feed items (public + authenticated)
   * - User              — user profiles and /me
   * - Notification      — in-app notification list + unread count
   * - Territory         — territory list (rarely mutated)
   * - Comment           — post comments
   * - Reaction          — per-post reactions
   * - Session           — user device sessions (/me/sessions/)
   * - Media             — uploaded media objects (presign + confirm flow)
   */
  tagTypes: [
    'Post',
    'PublicPost',
    'User',
    'Notification',
    'Territory',
    'Comment',
    'Reaction',
    'Session',
    // Polls embedded in Gladys announcements (vote + aggregated results)
    'Poll',
    // Follow system (Phase B / M1)
    'FollowStatus',
    'MyFollowings',
    'MyFollowers',
    'FollowStats',
    // Media uploads (Phase 3 / B1)
    'Media',
  ],
  // Re-fetch stale data when the user returns to the tab or regains network.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: () => ({}),
});
